import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../../utils/api';

const QuestionEditModal = ({ open, onClose, assessment, onSave }) => {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editText, setEditText] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (open && assessment) {
      loadQuestions();
    }
  }, [open, assessment]);

  const loadQuestions = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminAPI.getAssessmentQuestions(assessment.id);
      setQuestions(data.questions);
    } catch (err) {
      setError(err.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (question) => {
    setEditingQuestionId(question.id);
    setEditText(question.question_text);
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setEditText('');
  };

  const handleSaveQuestion = async (questionId) => {
    if (!editText.trim()) {
      setError(t('admin.questions.emptyTextError', 'Question text cannot be empty'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      const updated = await adminAPI.updateQuestion(questionId, editText.trim());

      setQuestions(prev => prev.map(q =>
        q.id === questionId ? { ...q, question_text: updated.question_text } : q
      ));

      setEditingQuestionId(null);
      setEditText('');
      setHasChanges(true);
    } catch (err) {
      setError(err.message || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges && onSave) {
      onSave();
    }
    setEditingQuestionId(null);
    setEditText('');
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('admin.questions.editTitle', 'Edit Questions')} - {assessment?.name}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            {questions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('admin.questions.noQuestions', 'No questions found.')}
              </Typography>
            ) : (
              questions.map((question, index) => (
                <Box key={question.id} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={question.order_number === 0 ? t('admin.questions.testQuestion', 'Test') : `Q${question.order_number}`}
                      size="small"
                      color={question.order_number === 0 ? 'warning' : 'primary'}
                    />
                    {editingQuestionId !== question.id && (
                      <IconButton
                        size="small"
                        onClick={() => handleStartEdit(question)}
                        title={t('common.edit', 'Edit')}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  {editingQuestionId === question.id ? (
                    <Box>
                      <TextField
                        multiline
                        rows={4}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        fullWidth
                        variant="outlined"
                        autoFocus
                      />
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={handleCancelEdit}
                          disabled={saving}
                        >
                          {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                          onClick={() => handleSaveQuestion(question.id)}
                          disabled={saving}
                        >
                          {t('common.save', 'Save')}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        p: 2,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        whiteSpace: 'pre-wrap'
                      }}
                    >
                      {question.question_text}
                    </Typography>
                  )}

                  {index < questions.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {t('common.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionEditModal;
